import { useEffect, useState, useRef } from "react";
import * as signalR from "@microsoft/signalr";

import Sidebar from "./Sidebar";
import ChatHeader from "./ChatHeader";
import ChatMessages from "./ChatMessages";
import MessageInput from "./MessageInput";
import WelcomeScreen from "./WelcomeScreen";
import NavBar from "../NavBar";
import { getAuthToken } from "../../../Auth/Services/tokenAuth";

import {
	getAllChats,
	getMessagesByPhone,
	sendMessageApi,
} from "../../Services/Home";

import { parseDate, formatTimeSmart } from "../../utils/dateUtils";
import { formatPhone } from "../../utils/formatUtils";

export default function Home() {
	const [recentChats, setRecentChats] = useState([]);
	const [selectedChat, setSelectedChat] = useState(null);
	const [loadingChats, setLoadingChats] = useState(true);

	const [messages, setMessages] = useState([]);
	const [input, setInput] = useState("");
	const [search, setSearch] = useState("");
	const [chatSearch, setChatSearch] = useState("");

	const [matchIndexes, setMatchIndexes] = useState([]);
	const [currentMatch, setCurrentMatch] = useState(0);

	const bottomRef = useRef(null);

	const upsertRecentChat = (prevChats, phone, updates = {}) => {
		const existingChat = prevChats.find((chat) => chat.name === phone);
		const remainingChats = prevChats.filter((chat) => chat.name !== phone);

		const nextChat = existingChat
			? {
					...existingChat,
					...updates,
			  }
			: {
					id: Date.now(),
					name: phone,
					displayName: formatPhone(phone),
					lastMessage: "",
					time: "",
					unread: 0,
					...updates,
			  };

		return [nextChat, ...remainingChats];
	};

	useEffect(() => {
		setLoadingChats(true);

		getAllChats()
			.then((res) => {
				const sorted = res.data.sort(
					(a, b) => parseDate(b.time) - parseDate(a.time)
				);

				setRecentChats(
					sorted.map((c, i) => ({
						id: i,
						name: c.phone,
						displayName: c.name?.trim()
							? `${c.name.trim()} (${formatPhone(c.phone)})`
							: c.wpName?.trim()
							? `${c.wpName.trim()} (${formatPhone(c.phone)})`
							: formatPhone(c.phone),
						lastMessage: c.lastMessage || "",
						time: formatTimeSmart(c.time),
						unread: 0,
					}))
				);
			})
			.catch((error) => {
				console.error("Unable to load chats", error);
				setRecentChats([]);
			})
			.finally(() => {
				setLoadingChats(false);
			});
	}, []);

	useEffect(() => {
		const handleEsc = (e) => {
			if (e.key === "Escape") {
				setSelectedChat(null);
				setMessages([]);
				setChatSearch("");
				setMatchIndexes([]);
				setCurrentMatch(0);
			}
		};

		window.addEventListener("keydown", handleEsc);
		return () => window.removeEventListener("keydown", handleEsc);
	}, []);

	const selectChat = async (chat) => {
		setSelectedChat(chat);

		setRecentChats((prev) =>
			prev.map((item) =>
				item.name === chat.name ? { ...item, unread: 0 } : item
			)
		);

		const res = await getMessagesByPhone(chat.name);

		setMessages(
			res.data.map((m, i) => ({
				id: i,
				messageId: m.messageId, // ✅ important
				text: m.content,
				time: m.time,
				from: m.direction === "outgoing" ? "user" : "bot",
				sentBy: m.sentBy,

				// ✅ status mapping
				status: m.isSeen
					? "read"
					: m.isDelivered
					? "delivered"
					: m.isSent
					? "sent"
					: "sending",
			}))
		);
	};

	const selectedChatRef = useRef(null);

	useEffect(() => {
		selectedChatRef.current = selectedChat;
	}, [selectedChat]);

	useEffect(() => {
		const connection = new signalR.HubConnectionBuilder()
			.withUrl("https://uat.o4shrsetu.com/chathub")
			.withAutomaticReconnect()
			.build();

		connection
			.start()
			.then(() => console.log("✅ SignalR Connected"))
			.catch((err) => console.error(err));

		// 📩 Incoming messages
		connection.on("ReceiveMessage", (phone, text, direction, messageId) => {
			if (direction === "outgoing") return;

			const newMsg = {
				id: Date.now(),
				messageId: messageId,
				text: text,
				time: new Date().toISOString(),
				from: "bot",
			};

			if (selectedChatRef.current?.name === phone) {
				setMessages((prev) => [...prev, newMsg]);

				setTimeout(() => {
					bottomRef.current?.scrollIntoView({ behavior: "smooth" });
				}, 100);
			}

			setRecentChats((prev) => {
				const existingChat = prev.find((chat) => chat.name === phone);

				return upsertRecentChat(prev, phone, {
					displayName:
						existingChat?.displayName || formatPhone(phone),
					lastMessage: newMsg.text,
					time: formatTimeSmart(newMsg.time),
					unread:
						selectedChatRef.current?.name === phone
							? 0
							: (existingChat?.unread || 0) + 1,
				});
			});
		});

		// ✅ STATUS UPDATE
		connection.on("MessageStatusUpdate", (messageId, status) => {
			setMessages((prev) =>
				prev.map((msg) =>
					msg.messageId === messageId
						? { ...msg, status: status }
						: msg
				)
			);
		});

		return () => {
			connection.stop();
		};
	}, []);

	useEffect(() => {
		if (!chatSearch.trim()) {
			setMatchIndexes([]);
			setCurrentMatch(0);
			return;
		}

		const matches = messages
			.map((m, i) =>
				m.text.toLowerCase().includes(chatSearch.toLowerCase()) ? i : -1
			)
			.filter((i) => i !== -1);

		setMatchIndexes(matches);
		setCurrentMatch(0);
	}, [chatSearch, messages]);

	useEffect(() => {
		if (matchIndexes.length > 0 && chatSearch.trim()) {
			const el = document.getElementById(
				`msg-${matchIndexes[currentMatch]}`
			);
			el?.scrollIntoView({ behavior: "smooth", block: "center" });
		}
	}, [currentMatch, matchIndexes, chatSearch]);

	useEffect(() => {
		if (!chatSearch.trim()) {
			bottomRef.current?.scrollIntoView({ behavior: "smooth" });
		}
	}, [messages, chatSearch]);

	const highlightText = (text, search) => {
		if (!search.trim()) return text;

		const parts = text.split(new RegExp(`(${search})`, "gi"));

		return parts.map((p, i) =>
			p.toLowerCase() === search.toLowerCase() ? (
				<span key={i} className="bg-yellow-300 px-1 rounded">
					{p}
				</span>
			) : (
				p
			)
		);
	};

	const sendMessage = async () => {
		if (!input.trim() || !selectedChat) return;

		const employeeCode = getEmployeeCodeFromToken();
		const employeeName = getEmployeeNameFromToken();

		const messageText = input.trim();
		setInput("");

		const tempId = Date.now().toString();

		const newMsg = {
			id: Date.now(),
			messageId: tempId,
			text: messageText,
			time: new Date().toISOString(),
			from: "user",
			sentBy: employeeName,
			status: "sending",
		};

		setMessages((prev) => [...prev, newMsg]);

		setRecentChats((prev) =>
			upsertRecentChat(prev, selectedChat.name, {
				displayName: selectedChat.displayName,
				lastMessage: messageText,
				time: formatTimeSmart(newMsg.time),
				unread: 0,
			})
		);

		try {
			await sendMessageApi({
				phone: selectedChat.name,
				message: messageText,
				sentBy: employeeCode,
			});

			// ✅ mark as sent
			setMessages((prev) =>
				prev.map((m) =>
					m.messageId === tempId
						? { ...m, status: "sent" }
						: m
				)
			);
		} catch (error) {
			console.error("Unable to send message", error);
			setInput(messageText);
		}
	};

	const getEmployeeCodeFromToken = () => {
		try {
			const token = getAuthToken();
			if (!token) return null;

			const payload = JSON.parse(atob(token.split(".")[1]));
			return payload.EmployeeCode || null;
		} catch {
			return null;
		}
	};

	const getEmployeeNameFromToken = () => {
		try {
			const token = getAuthToken();
			if (!token) return null;

			const payload = JSON.parse(atob(token.split(".")[1]));
			return payload.CLSName || null;
		} catch {
			return null;
		}
	};

	return (
		<div className="h-screen flex overflow-hidden">
			<NavBar active="chats" />

			<div className="w-[400px] min-h-0 border-r bg-white">
				<Sidebar
					recentChats={recentChats}
					selectedChat={selectedChat}
					selectChat={selectChat}
					search={search}
					setSearch={setSearch}
					loading={loadingChats}
				/>
			</div>

			<div className="flex-1 min-h-0 flex flex-col">
				<ChatHeader
					selectedChat={selectedChat}
					chatSearch={chatSearch}
					setChatSearch={setChatSearch}
					matchIndexes={matchIndexes}
					currentMatch={currentMatch}
					setCurrentMatch={setCurrentMatch}
				/>

				{selectedChat ? (
					<>
						<ChatMessages
							messages={messages}
							matchIndexes={matchIndexes}
							currentMatch={currentMatch}
							chatSearch={chatSearch}
							highlightText={highlightText}
							bottomRef={bottomRef}
						/>

						<MessageInput
							input={input}
							setInput={setInput}
							sendMessage={sendMessage}
						/>
					</>
				) : (
					<WelcomeScreen />
				)}
			</div>
		</div>
	);
}